import i18next, { InitOptions, ReactOptions } from 'i18next';
import LanguageDetectorModule from 'i18next-browser-languagedetector';
import HttpBackend from 'i18next-http-backend';
import { default as LocizeBackendModule, LocizeBackendOptions } from 'i18next-locize-backend';
import { default as LocizeLastUsedModule } from 'locize-lastused';
import { initReactI18next as ReactModule } from 'react-i18next';

import { availableLanguages, defaultLanguage } from './i18n.constants';
import { i18nLanguage } from './i18nLanguage.type';

import { changeDateLocale, changeNumberLocale } from '../utils';

const logger = (msg: string) => {
  console.debug('[i18n]', msg);
};

const reactOptions: ReactOptions = {
  bindI18n: 'languageChanged loaded',
  useSuspense: false, // need to use <Suspense /> react component wrapped around app if `true` - will then always suspense when a new file i18n file is loaded
};

const i18nextOptions: InitOptions = {
  defaultNS: 'common',
  ns: ['common', 'global', 'navigation', 'notification', 'type', 'validation'],
  fallbackLng: {
    // exception for norwegian language having multiple language codes: nb and nn should fall back to no
    nb: ['no'],
    nn: ['no'],
    default: [defaultLanguage.code],
  },
  supportedLngs: ['nn', 'nb', ...availableLanguages.map((lng) => lng.code)], // exception for norwegian language having multiple language codes: nn and nb need to be supported languages otherwise the fallback does not work
  load: 'languageOnly',
  saveMissing: false,
  debug: false,
  interpolation: {
    escapeValue: false,
  },
  react: reactOptions,
  detection: {
    order: ['localStorage', 'navigator'],
    caches: ['localStorage'],
  },
  returnNull: false,
};

type InitI18nOptions = Pick<LocizeBackendOptions, 'projectId' | 'apiKey'>;

export const initI18n = async ({ projectId, apiKey }: InitI18nOptions): Promise<void> => {
  logger('Initializing i18next ...');

  const locizeOptions: LocizeBackendOptions = {
    projectId,
    apiKey,
    version: 'latest',
    allowedAddOrUpdateHosts: () => false,
  };

  i18nextOptions.backend = locizeOptions;

  const locizeLastusedOptions = {
    projectId,
    // submit interval in ms
    debounceSubmit: 5000,
  };

  i18nextOptions.locizeLastUsed = locizeLastusedOptions;

  const initI18nPromise = i18next
    .use(LocizeBackendModule)
    .use(LocizeLastUsedModule)
    .use(LanguageDetectorModule)
    .use(ReactModule)
    .init(i18nextOptions);

  // if promise is resolved, i18n.language will be set by language detector (otherwise he will check for i18nextLgn in local storage)
  await initI18nPromise;

  logger('Initialized i18next');
};

export const initI18nLocal = async (): Promise<void> => {
  logger('Initializing i18next ...');

  const initI18nPromise = i18next
    .use(HttpBackend)
    .use(LanguageDetectorModule)
    .use(ReactModule)
    .init({
      ...i18nextOptions,
      fallbackLng: {
        nb: ['no'],
        nn: ['no'],
        default: ['no'],
      },
      supportedLngs: ['nn', 'nb', 'no'],
      backend: {
        loadPath: '/assets/i18n/{{lng}}/{{ns}}.json',
      },
    });

  await initI18nPromise;
  logger('Initialized i18next');
};
/**
 * Will return available (configured) i18nLanguage if code is existing within `availableLanguages`
 */
export const getI18nLanguage = (code?: string): i18nLanguage => {
  const countryCode = code?.split('-')[0];
  return (
    availableLanguages.find((lng) => typeof countryCode === 'string' && lng.code === countryCode.toLowerCase()) ??
    defaultLanguage
  );
};

/**
 * Receive current resolved language as i18nLanguage (will either be current detected or set - stored - language)
 */
export const getCurrentI18nLanguage = (): i18nLanguage => {
  return getI18nLanguage(i18next.resolvedLanguage);
};

/**
 * Will change currently set i18n language by providing a valid configured language code
 */
export const changeI18nLanguage = async (code?: string): Promise<i18nLanguage | undefined> => {
  logger('Changing i18n language ...');

  const language = getI18nLanguage(code);
  try {
    await i18next.changeLanguage(language.code);
  } catch {
    return;
  }
  document.documentElement.setAttribute('lang', language.code);
  changeDateLocale(language.dateLocale);
  changeNumberLocale(language.numberLocale);

  logger('Changed i18n language');
  return language;
};

/**
 * Resets language to default configured language
 * @returns Promise
 */
export const resetI18nLanguage = () => changeI18nLanguage(getCurrentI18nLanguage().code);

export default i18next;
export const i18n = i18next;
